/**
 * fb#609 — ขอเปลี่ยนเวลาเข้า-ออกงาน (มุมพนักงาน) รายวัน
 * เช่น กะ 09:00–18:00 ขอวันที่ X เป็น 07:00–16:00 · ชั่วโมงเท่าเดิม · ไม่แตะตารางกะ
 * เลือกวัน → เห็นกะเดิม → กรอกเวลาเข้าใหม่ → ระบบคำนวณเวลาออกให้ · ต้องมีเหตุผล
 * ย้อนหลังได้เฉพาะงวดที่ยังไม่ปิด (เซิร์ฟเวอร์ตรวจ) · อนุมัติโดยหัวหน้า/HR ที่หน้า "อนุมัติคำขอ"
 */
import { useState } from 'react';
import { Clock4, Send, ArrowRight, AlertTriangle } from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { useMyShiftTimes, useCreateShiftTime, useCancelShiftTime, useMyShiftForTime, type ShiftTimeRow } from '../hooks';
import { useToast } from '../components/ui/toast';

const STATUS: Record<ShiftTimeRow['status'], { label: string; cls: string }> = {
  pending: { label: 'รอหัวหน้า/HR อนุมัติ', cls: 'badge-warn' },
  approved: { label: 'อนุมัติแล้ว', cls: 'badge-success' },
  rejected: { label: 'ไม่อนุมัติ', cls: 'badge-danger' },
  cancelled: { label: 'ยกเลิก', cls: 'badge-danger' },
};

function fmtThai(iso: string): string {
  return new Intl.DateTimeFormat('th-TH', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Bangkok' })
    .format(new Date(`${iso}T00:00:00+07:00`));
}
const toMin = (t: string) => { const m = /^(\d{1,2}):(\d{2})/.exec(t); return m ? Number(m[1]) * 60 + Number(m[2]) : NaN; };
const fromMin = (n: number) => `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;

export function ShiftTime() {
  const mine = useMyShiftTimes();
  const create = useCreateShiftTime();
  const cancel = useCancelShiftTime();
  const toast = useToast();
  const [workDate, setWorkDate] = useState('');
  const [newStart, setNewStart] = useState('');
  const [reason, setReason] = useState('');
  const { data: resolved, isFetching } = useMyShiftForTime(workDate || undefined);
  const shift = resolved?.shift ?? null;

  // ข้อความบอกว่าวันนี้ขอได้ไหม (เซิร์ฟเวอร์ตรวจซ้ำทุกข้อ)
  const blocked = !workDate || isFetching ? null
    : !shift ? 'วันนี้เป็นวันหยุดตามตาราง — ขอได้เฉพาะวันที่มีกะทำงาน'
      : shift.time_override ? 'วันนี้มีใบเปลี่ยนเวลาที่อนุมัติแล้ว'
        : shift.flexible ? 'กะยืดหยุ่น เข้าเวลาไหนก็ได้อยู่แล้ว ไม่ต้องขอ'
          : shift.crosses_midnight || toMin(shift.end_time) <= toMin(shift.start_time) ? 'กะข้ามคืนยังขอผ่านระบบไม่ได้ — ให้แจ้ง HR'
            : null;
  const len = shift ? toMin(shift.end_time) - toMin(shift.start_time) : NaN;
  const startMin = toMin(newStart);
  const endMin = startMin + len;
  const newEnd = shift && !blocked && !Number.isNaN(startMin) && endMin < 1440 ? fromMin(endMin) : null;
  const sameAsShift = !!shift && startMin === toMin(shift.start_time);
  const overMidnight = !!shift && !blocked && !Number.isNaN(startMin) && endMin >= 1440;
  const reasonOk = reason.trim().length >= 5;
  const canSubmit = !!newEnd && !sameAsShift && reasonOk && !create.isPending;

  function submit() {
    if (!canSubmit) return;
    create.mutate(
      { work_date: workDate, new_start_time: newStart, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success('ส่งคำขอแล้ว — รอหัวหน้า/HR อนุมัติ');
          setWorkDate(''); setNewStart(''); setReason('');
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'ส่งคำขอไม่สำเร็จ'),
      },
    );
  }

  function cancelRow(id: string) {
    cancel.mutate({ id }, {
      onSuccess: () => toast.success('ยกเลิกคำขอแล้ว'),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'ยกเลิกไม่สำเร็จ'),
    });
  }

  const rows = mine.data ?? [];
  const hrs = Number.isNaN(len) ? '' : `${Math.floor(len / 60)} ชม.${len % 60 ? ` ${len % 60} นาที` : ''}`;

  return (
    <PageLayout title="ขอเปลี่ยนเวลาเข้า-ออกงาน">
      <div className="space-y-5 pb-10">
        <div className="card p-4 space-y-3">
          <div className="font-medium text-gray-800 flex items-center gap-1.5">
            <Clock4 className="w-4 h-4 text-teal" /> เปลี่ยนเวลาเข้า-ออกงานเฉพาะวัน
          </div>
          <p className="text-xs text-gray-500">
            สำหรับกรณีจำเป็น เช่น เข้าเร็วเพื่อออกเร็ว — ยังต้องทำงาน <b>ครบชั่วโมงเท่ากะเดิม</b> ·
            ตารางกะไม่เปลี่ยน · ต้องระบุเหตุผล หัวหน้า/HR พิจารณาเป็นรายคน · ย้อนหลังได้เฉพาะงวดเงินเดือนที่ยังไม่ปิด
          </p>
          <div>
            <label className="label">วันที่</label>
            <input type="date" value={workDate} onChange={(e) => setWorkDate(e.target.value)} className="input w-full" />
          </div>

          {workDate && (
            <div className="rounded-lg bg-gray-50 p-3 text-sm">
              {isFetching ? (
                <span className="text-gray-400">กำลังโหลดกะของวันนี้…</span>
              ) : shift ? (
                <span>กะเดิม: <b>{shift.name}</b> {shift.time_override ? `${shift.time_override.orig_start_time}–${shift.time_override.orig_end_time}` : `${shift.start_time}–${shift.end_time}`}{hrs && ` (${hrs})`}</span>
              ) : (
                <span className="text-gray-500">ไม่มีกะ (วันหยุด)</span>
              )}
            </div>
          )}

          {blocked && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{blocked}</span>
            </div>
          )}

          {shift && !blocked && (
            <>
              <div>
                <label className="label">เวลาเข้างานใหม่</label>
                <input type="time" value={newStart} onChange={(e) => setNewStart(e.target.value)} className="input w-full" />
              </div>
              <div className="rounded-lg border border-teal-200 bg-teal-50 p-3 text-sm flex items-center gap-2 flex-wrap">
                <span className="text-gray-600">เวลาใหม่:</span>
                <b>{newStart || '--:--'}</b>
                <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                <b>{newEnd ?? '--:--'}</b>
                <span className="text-xs text-gray-500">(ระบบคำนวณเวลาออกให้ = เวลาเข้า + {hrs})</span>
              </div>
              {sameAsShift && <p className="text-xs text-amber-700">เวลาเข้าใหม่ตรงกับกะเดิม — ไม่ต้องขอเปลี่ยน</p>}
              {overMidnight && <p className="text-xs text-amber-700">เวลาออกจะเลยเที่ยงคืน — เลือกเวลาเข้าที่เร็วกว่านี้</p>}
              <div>
                <label className="label">เหตุผลที่จำเป็น</label>
                <textarea
                  value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={300}
                  placeholder="เช่น ต้องไปรับลูกที่โรงเรียนช่วงบ่าย"
                  className={`input w-full ${reason && !reasonOk ? 'border-amber-400' : ''}`}
                />
                {reason && !reasonOk && <p className="text-xs text-amber-700 mt-1">ระบุเหตุผลอย่างน้อย 5 ตัวอักษร</p>}
              </div>
              <button onClick={submit} disabled={!canSubmit} className="btn-cta w-full justify-center flex items-center gap-2">
                <Send className="w-4 h-4" /> {create.isPending ? 'กำลังส่ง…' : 'ส่งคำขอ'}
              </button>
            </>
          )}
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700">คำขอของฉัน</div>
          {mine.isLoading ? (
            <p className="text-sm text-gray-400">กำลังโหลด…</p>
          ) : mine.isError ? (
            <p className="text-sm text-red-600">โหลดคำขอไม่สำเร็จ</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-400">ยังไม่มีคำขอ</p>
          ) : (
            rows.map((r) => (
              <div key={r.id} className="card p-3 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm text-gray-800">{fmtThai(r.work_date)}</div>
                  <span className={`${STATUS[r.status].cls} shrink-0 whitespace-nowrap`}>{STATUS[r.status].label}</span>
                </div>
                <div className="text-sm text-gray-700 flex items-center gap-1.5 flex-wrap">
                  <span className="text-gray-400 line-through">{r.orig_start_time}–{r.orig_end_time}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                  <b>{r.new_start_time}–{r.new_end_time}</b>
                </div>
                <p className="text-xs text-gray-500">เหตุผล: {r.reason}</p>
                {(r.status === 'rejected' || r.status === 'cancelled') && r.rejection_reason && (
                  <p className="text-xs text-red-600">{r.status === 'rejected' ? 'เหตุผลที่ไม่อนุมัติ' : 'เหตุผลที่ยกเลิก'}: {r.rejection_reason}</p>
                )}
                {r.status === 'pending' && (
                  <button onClick={() => cancelRow(r.id)} disabled={cancel.isPending} className="btn-secondary text-sm min-h-[44px]">
                    ยกเลิกคำขอ
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </PageLayout>
  );
}
